import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import Sidebar from "../components/layout/Sidebar";
import PersonalInfoSection from "../components/personal-info/PersonalInfoSection";
import PasswordSection from "../components/profile/PasswordSection";
import AvailabilitySection from "../components/profile/AvailabilitySection";
import DocumentsSection from "../components/profile/DocumentsSection";
import ZoomSection from "../components/profile/ZoomSection";
import TabButton from "../components/common/TabButton";
import ProfilePhotoSection from "../components/profile/ProfilePhotoSection";

import { ProfilePhotoProvider } from "../contexts/ProfilePhotoContext";
import ProfileHeader from "../components/ui/ProfileHeader";
import { useUser } from "../contexts/UserContext";
import { DocumentsProvider } from "../contexts/DocumentsContext";

const CONTAINER = "mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8";

const SECTION_REFS_MAP = (refs) => ({
  personal: refs.personal,
  availability: refs.availability,
  documents: refs.documents,
  zoom: refs.zoom,
  password: refs.password,
  photo: refs.photo,
});

const Profile = () => {
  const navigate = useNavigate();
  const { user, userType, ready } = useUser();

  const effectiveRole = user?.role || userType || "student";
  const isTeacher = effectiveRole === "teacher";
  const isSupplier = effectiveRole === "supplier";
  const isSchool = effectiveRole === "school";
  const canUseDocuments = isTeacher || isSupplier;
  const showPassword = (user?.auth || "normal") === "normal";

  const tabs = useMemo(() => {
    const t = [{
      id: "personal",
      label: isSupplier ? "פרטי מרכז הלמידה" : isSchool ? "פרטי בית הספר" : "פרטים אישיים",
    }];

    if (canUseDocuments) {
      t.push({ id: "documents", label: "מסמכים" });
    }

    if (isTeacher) {
      t.push(
        { id: "availability", label: "זמני זמינות" },
        { id: "zoom", label: "זום" }
      );
    }

    if (showPassword) {
      t.push({ id: "password", label: "סיסמה" });
    }

    return t;
  }, [isTeacher, isSupplier, isSchool, canUseDocuments, showPassword]);

  const [activeTab, setActiveTab] = useState("personal");

  const refs = {
    personal: useRef(null),
    availability: useRef(null),
    documents: useRef(null),
    zoom: useRef(null),
    password: useRef(null),
    photo: useRef(null),
  };

  useEffect(() => {
    if (ready && !user) {
      navigate("/AuthPage", { replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    let raf1 = null;
    let raf2 = null;

    const previousScrollRestoration =
      "scrollRestoration" in window.history
        ? window.history.scrollRestoration
        : null;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    window.scrollTo(0, 0);

    raf1 = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      raf2 = requestAnimationFrame(() => window.scrollTo(0, 0));
    });

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);

      if (previousScrollRestoration !== null) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, []);

  useEffect(() => {
    const allowed = new Set([...tabs.map((t) => t.id), "photo"]);

    if (activeTab !== null && !allowed.has(activeTab)) {
      setActiveTab(null);
    }
  }, [tabs, activeTab]);

  const scrollToSection = (id) => {
    setActiveTab(id);

    requestAnimationFrame(() => {
      const sectionRef = SECTION_REFS_MAP(refs)[id];

      sectionRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-sm">
        טוען פרופיל…
      </div>
    );
  }

  if (!user) {
    return <div className="min-h-screen bg-gray-100" />;
  }

  return (
    <div dir="rtl" className="min-h-screen bg-gray-100 font-sans pb-10">
      <ProfileHeader />

      <main className={CONTAINER}>
        <div className="flex flex-wrap gap-1 mb-5 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              label={tab.label}
              active={activeTab === tab.id}
              onClick={() => scrollToSection(tab.id)}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
          <div className="order-1">
            <Sidebar scrollToSection={scrollToSection} />
          </div>

          <div className="order-2 space-y-0">
            <PersonalInfoSection
              ref={refs.personal}
              active={activeTab === "personal"}
            />

            {showPassword && (
              <PasswordSection
                ref={refs.password}
                active={activeTab === "password"}
              />
            )}

            {isTeacher && (
              <AvailabilitySection
                ref={refs.availability}
                active={activeTab === "availability"}
              />
            )}

            {canUseDocuments && (
              <DocumentsProvider>
                <DocumentsSection
                  ref={refs.documents}
                  active={activeTab === "documents"}
                />
              </DocumentsProvider>
            )}

            {isTeacher && (
              <ZoomSection
                ref={refs.zoom}
                active={activeTab === "zoom"}
              />
            )}

            <ProfilePhotoProvider userId={user?.id}>
              <ProfilePhotoSection
                ref={refs.photo}
                active={activeTab === "photo"}
              />
            </ProfilePhotoProvider>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;